/**
 * FREELANCERS DA AGENDA — o cadastro de quem é de fora, e ele mora SÓ AQUI.
 *
 * Não é uma aba, não é o RH/DP, não é a Equipe: é um botão da barra da agenda,
 * porque é só na escala que esse nome precisa existir. O freelancer não tem
 * admissão, cargo, benefício nem login — o que ele tem é um nome numa linha da
 * grade e, no máximo, um telefone para chamar. Cadastrá-lo como colaborador não
 * era opção: aquela tabela é espelho do RH/DP (criar dá 403 e o sincronismo
 * apagaria a linha na volta seguinte). Ver server/agenda-freelancers.ts.
 *
 * NÃO EXISTE EXCLUIR, e é escolha: apagar levaria junto, em cascata, todos os
 * dias que a pessoa já trabalhou. "Tirar da agenda" (inativar) some com o nome
 * da grade e preserva o histórico — que é o que se quer quando o contrato
 * acaba, e é reversível.
 *
 * Fica em arquivo próprio, e não dentro do AgendaView, porque componente
 * declarado dentro de outro é remontado a cada tecla e o campo perde o foco no
 * meio da frase (o tropeço já corrigido na AvaliacaoPublica do RH).
 */
import { useState } from "react";
import type { Row } from "../types";
import { api } from "../api";
import { IconClose } from "../icons";
import { ordenarPorNome } from "../ordem";

export default function FreelancersModal({
  lista,
  onClose,
  onMudou,
}: {
  lista: Row[];
  onClose: () => void;
  /** Recarrega a agenda: a grade ganha ou perde uma linha. */
  onMudou: () => Promise<void> | void;
}) {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [observacao, setObservacao] = useState("");
  /** Id em edição (null = o formulário está criando um novo). */
  const [editando, setEditando] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Em ordem alfabética, como a grade desenha as linhas (src/ordem.ts).
  const ativos = ordenarPorNome(
    lista.filter((f) => f.ativo !== false),
    (f) => f.nome,
  );
  const inativos = ordenarPorNome(
    lista.filter((f) => f.ativo === false),
    (f) => f.nome,
  );

  function limpar() {
    setEditando(null);
    setNome("");
    setTelefone("");
    setObservacao("");
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    const limpo = nome.trim();
    if (limpo === "" || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      const dados = { nome: limpo, telefone: telefone.trim(), observacao: observacao.trim() };
      if (editando != null) await api.update("freelancers", editando, dados);
      else await api.create("freelancers", { ...dados, ativo: true });
      limpar();
      await onMudou();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível salvar o freelancer.");
    } finally {
      setSalvando(false);
    }
  }

  async function alternarAtivo(f: Row) {
    if (salvando) return;
    const trazendoDeVolta = f.ativo === false;
    if (
      !trazendoDeVolta &&
      !confirm(
        `Tirar ${f.nome} da agenda?\n\n` +
          "A linha sai da grade, mas os dias que ele já trabalhou continuam no histórico. " +
          "Dá para trazer de volta a qualquer momento.",
      )
    ) {
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await api.update("freelancers", Number(f.id), { ativo: trazendoDeVolta });
      if (editando === Number(f.id)) limpar();
      await onMudou();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível alterar o freelancer.");
    } finally {
      setSalvando(false);
    }
  }

  function editar(f: Row) {
    setEditando(Number(f.id));
    setNome(String(f.nome ?? ""));
    setTelefone(String(f.telefone ?? ""));
    setObservacao(String(f.observacao ?? ""));
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card agenda-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Freelancers da agenda</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="muted" style={{ marginTop: 0 }}>
            Quem entra aqui vira uma linha na grade e recebe serviço, cor e observações como
            qualquer outro. Não aparece no RH/DP, na aba Equipe, no aplicativo do colaborador nem
            nos indicadores.
          </p>

          <form className="agenda-freela-form" onSubmit={salvar}>
            <label className="field">
              <span>Nome{editando != null ? " (editando)" : ""}</span>
              <input
                autoFocus
                value={nome}
                placeholder="Ex.: João da Silva"
                onChange={(e) => setNome(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Telefone</span>
              <input
                value={telefone}
                placeholder="Ex.: (19) 99999-0000"
                onChange={(e) => setTelefone(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Observação</span>
              <input
                value={observacao}
                placeholder="Ex.: eletricista — contrato até dezembro"
                onChange={(e) => setObservacao(e.target.value)}
              />
            </label>
            <div className="agenda-freela-form-acoes">
              {editando != null && (
                <button type="button" className="btn-secondary" onClick={limpar} disabled={salvando}>
                  Cancelar edição
                </button>
              )}
              <button
                type="submit"
                className="btn-primary"
                disabled={salvando || nome.trim() === ""}
              >
                {salvando ? "Salvando..." : editando != null ? "Salvar alterações" : "+ Adicionar"}
              </button>
            </div>
          </form>

          {erro && <div className="form-error">{erro}</div>}

          <div className="agenda-freela-lista">
            {ativos.length === 0 && inativos.length === 0 ? (
              <p className="muted">Nenhum freelancer cadastrado ainda.</p>
            ) : (
              <>
                {ativos.map((f) => (
                  <div className="agenda-freela-item" key={f.id}>
                    <div>
                      <strong>{f.nome}</strong>
                      {f.telefone ? <span className="muted"> · {f.telefone}</span> : null}
                      {f.observacao ? <p className="muted">{f.observacao}</p> : null}
                    </div>
                    <div className="agenda-freela-acoes">
                      <button type="button" className="agenda-link" onClick={() => editar(f)}>
                        editar
                      </button>
                      <button
                        type="button"
                        className="agenda-link"
                        disabled={salvando}
                        onClick={() => void alternarAtivo(f)}
                      >
                        tirar da agenda
                      </button>
                    </div>
                  </div>
                ))}

                {inativos.length > 0 && (
                  <>
                    <p className="agenda-freela-sep">Fora da agenda</p>
                    {inativos.map((f) => (
                      <div className="agenda-freela-item agenda-freela-inativo" key={f.id}>
                        <div>
                          <strong>{f.nome}</strong>
                          {f.telefone ? <span className="muted"> · {f.telefone}</span> : null}
                        </div>
                        <div className="agenda-freela-acoes">
                          <button
                            type="button"
                            className="agenda-link"
                            disabled={salvando}
                            onClick={() => void alternarAtivo(f)}
                          >
                            trazer de volta
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
